import { isUndefined } from 'lodash'
import { getIdOrNull, getMatrix, isNonEmptyArray, isObject } from '../../../utils'
import { Internship, SCHEDULE_VALIDATED } from '../../../utils/entities/internship'
import { MONTH_STRING_FORMAT, WEEK_NUMBER_FORMAT } from '../../../utils/momentjs'
import { PeriodTypes } from '../../../utils/timePeriods'
import { Types } from '../../antd/Tables/DragTable/ResizingDiv'
import { cellDisplacement, getXCoordinates, getYCoordinates, isEmptySubMatrix } from '../../antd/Tables/utils'
import { Coordinates, getLaneKey, INCLUSION_LINE_KEY, LaneTypes } from '../Quotas/utils'
import { localStorage } from '../../../utils/localStorage'
import { Events } from '../../../utils/events'

export const getInternshipsPeriods = ({ startDate, type, quantity }, translator) => {
  const periods = {
    months: [],
    weeks: []
  }
  const step = PeriodTypes.WEEK

  let iterationDate = startDate.clone()
  let currentMonth = {
    month: iterationDate.month(),
    label: translator(iterationDate.format(MONTH_STRING_FORMAT)),
    weeks: []
  }

  for (let iStep = 0; iStep < quantity; iStep++) {
    currentMonth.weeks.push({
      startDate: iterationDate.clone().startOf(step),
      endDate: iterationDate.clone().endOf(step),
      number: iterationDate.format(WEEK_NUMBER_FORMAT)
    })

    periods.weeks.push(iterationDate.clone())
    iterationDate = iterationDate.clone().add(1, step)

    if (iterationDate.month() !== currentMonth.month) {
      periods.months.push(currentMonth)

      currentMonth = {
        month: iterationDate.month(),
        label: translator(iterationDate.format(MONTH_STRING_FORMAT)),
        weeks: []
      }
    }
  }

  if (currentMonth.weeks.length > 0) {
    periods.months.push(currentMonth)
  }

  return periods
}

const formatInternship = (weeks, matrix, sectionGroupsMap, getValue, getConfig) => (internship, index) => {
  const sectionId = isObject(internship.section) && isObject(internship.section.sectionToYear) ? getIdOrNull(internship.section.sectionToYear.section) : -1

  const laneConfig = sectionGroupsMap[sectionId] ? { type: LaneTypes.GROUP, id: sectionGroupsMap[sectionId] } : { type: LaneTypes.SECTION, id: sectionId }
  const laneKey = getLaneKey(laneConfig.type, laneConfig)

  const key = isUndefined(matrix[laneKey]) ? INCLUSION_LINE_KEY : laneKey

  const x = getXCoordinates(internship, weeks)

  return ({
    id: internship.id,
    key,
    x,
    alias: index + 1,
    value: getValue(internship),
    config: getConfig(internship),
    type: internship.state === SCHEDULE_VALIDATED ? Types.CLEAR : Types.SOLID,
    internship
  })
}

const sortByImportance = (storedInternships) => (a, b) => {
  if (storedInternships[a.id] && !storedInternships[b.id]) {
    return -1
  } else if (!storedInternships[a.id] && storedInternships[b.id]) {
    return 1
  }

  const aPrio = a.internship.isException ? -1 : 1
  const bPrio = b.internship.isException ? -1 : 1

  if (bPrio - aPrio !== 0) {
    return bPrio - aPrio
  }

  const startPrio = a.x[0] - b.x[0]

  if (startPrio !== 0) {
    return startPrio
  }

  const aLength = a.x[1] - a.x[0]
  const bLength = b.x[1] - b.x[0]

  return bLength - aLength
}

export const getInternshipsMatrix = (lines, weeks, sectionGroups, getConfig, getValue, storageKey) => {
  const internshipsAcc = lines.reduce((acc, line) => {
    acc.data[line.id] = []
    acc.matrix[line.id] = getMatrix(weeks.length, 1)
    acc.places[line.id] = line.places

    return acc
  }, { data: {}, matrix: {}, places: {} })

  const sectionGroupsMap = sectionGroups.reduce((acc, group) => {
    group.sections.forEach(section => {
      acc[getIdOrNull(section)] = group.id
    })

    return acc
  }, {})

  let storedInternships = {}

  if (storageKey && localStorage.has(storageKey)) {
    storedInternships = localStorage.get(storageKey)
  }

  lines.forEach(line => {
    line.internships
      .map(formatInternship(weeks, internshipsAcc.matrix, sectionGroupsMap, getValue, getConfig))
      .sort(sortByImportance(storedInternships))
      .forEach(data => {
        const { key, x } = data

        const isOverflow = key === INCLUSION_LINE_KEY && internshipsAcc.places[key] === 0
        let offset = isOverflow ? 1 : 0
        const matrix = isNonEmptyArray(internshipsAcc.matrix[key]) ? internshipsAcc.matrix[key] : getMatrix(weeks.length, 1)
        let y

        if (data.internship.isException) {
          offset += line.places
        }

        if (storedInternships[data.id]) {
          y = storedInternships[data.id].y
        } else {
          y = getYCoordinates({ data, total: 1 }, matrix, x, offset)
        }

        /** @TODO improve algorithm to complete and fill in only one action */
        if (y[1] >= internshipsAcc.matrix[key].length) {
          for (let i = internshipsAcc.matrix[key].length; i <= y[1]; i++) {
            internshipsAcc.matrix[key].push(new Array(weeks.length).fill(0))
          }
        }

        const width = x[1] >= weeks.length ? weeks.length - 1 : x[1]

        for (let i = y[0]; i <= y[1]; i++) {
          for (let j = x[0]; j <= width; j++) {
            internshipsAcc.matrix[key][i][j] = data.alias
          }
        }

        data.y = y

        internshipsAcc.data[key].push(data)
      })
  })

  const overflows = lines.reduce((acc, line) => {
    const overflow = internshipsAcc.matrix[line.id].length - Math.max(1, line.places)

    acc[line.id] = Math.max(overflow, 0)
    return acc
  }, {})

  return { data: internshipsAcc.data, overflows, matrix: internshipsAcc.matrix }
}

export const reduceBySector = (data, sectors = []) => {
  const seedMap = sectors.reduce((acc, sector) => {
    acc[getIdOrNull(sector) ?? -1] = []

    return acc
  }, {})

  return data.reduce((acc, d) => {
    const key = getIdOrNull(d.sector) ?? -1

    if (!acc[key]) {
      acc[key] = []
    }

    acc[key].push(d)

    return acc
  }, seedMap)
}

export const reduceByDepartment = (data, departments) => {
  const mappedBySector = reduceBySector(data, departments.sectors)

  const map = departments.reduce((acc, dept) => {
    const deptId = dept.id
    const sectorIds = dept.sectors.map(getIdOrNull)

    acc[deptId] = {}

    sectorIds.forEach(sectorId => {
      acc[deptId][sectorId] = mappedBySector[sectorId] ?? []
    })

    return acc
  }, {})

  return map
}

export const mapByDepartment = (data, departments) => departments.reduce((acc, department) => {
  const deptId = department.id

  acc[deptId] = {}

  Object.keys(data).forEach(sectorId => {
    if (!acc[deptId][sectorId]) {
      acc[deptId][sectorId] = []
    }

    acc[deptId][sectorId] = data[sectorId]
  })

  return acc
}, {})

export const getDifference = (a, b) => {
  return a.filter(x => !b.some(y => x.id === y.id))
}

export const getDivStyle = ({ background, border, text }) => ({
  backgroundColor: background,
  borderColor: border,
  color: text
})

export const internshipEvent = (data, matrix, storageKey, event, seed, rawInternship) => {
  const internship = rawInternship ? new Internship({ ...rawInternship.internship, ...seed }) : null
  const oldInternship = data.find(d => d.id === rawInternship.id)

  if (internship !== null) {
    const coordinates = new Coordinates(rawInternship)
    const oldCoordinates = new Coordinates(oldInternship ?? { x: [-1, -1], y: [-1, -1] })

    const spaceIsFree = isEmptySubMatrix(matrix, coordinates.y[0], coordinates.y[1], coordinates.x[0], coordinates.x[1], rawInternship.alias)

    if (!spaceIsFree && matrix.length > 0) {
      const newPositions = cellDisplacement(matrix, data ?? [], { ...coordinates, alias: rawInternship.alias })

      Object.keys(newPositions).forEach(alias => {
        const internshipData = data.find(d => d.alias === Number(alias))

        if (internshipData) {
          localStorage.add(storageKey, { key: internshipData.id, value: newPositions[alias] })
        }
      })
    }

    if (coordinates.isOnlyVerticalDisplacement(oldCoordinates)) {
      event = Events.STRICT_VERTICAL_DRAG_STOP
    }

    localStorage.add(storageKey, { key: internship.id, value: coordinates })
  }

  return { internship, event }
}
